import React, { useState, useEffect, useCallback } from 'react';
import api from './api';
import Spinner from './Spinner';
import CustomAlert from './CustomAlert';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        bg: isDark ? '#121212' : '#f4f7f6',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
    };

    return {
        container: { 
            backgroundColor: colors.cardBg, 
            borderRadius: '8px', 
            overflow: 'hidden', 
            boxShadow: `0 2px 5px ${colors.shadow}`,
            marginTop: '20px'
        },
        header: {
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa',
            padding: '15px 20px',
            borderBottom: `2px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        title: { 
            fontSize: '18px', 
            fontWeight: 'bold', 
            color: colors.text, 
            margin: 0 
        },
        controlsContainer: { 
            display: 'flex', 
            gap: '15px', 
            marginBottom: '20px', 
            flexWrap: 'wrap', 
            alignItems: 'center',
            padding: '15px 20px',
            borderBottom: `1px solid ${colors.border}`
        },
        searchInput: { 
            padding: '8px 12px', 
            borderRadius: '4px', 
            border: `1px solid ${colors.border}`, 
            backgroundColor: isDark ? '#333' : '#fff', 
            color: colors.text, 
            minWidth: '200px' 
        },
        filterSelect: { 
            padding: '8px 12px', 
            borderRadius: '4px', 
            border: `1px solid ${colors.border}`, 
            backgroundColor: isDark ? '#333' : '#fff', 
            color: colors.text 
        },
        table: { 
            width: '100%', 
            borderCollapse: 'collapse' 
        },
        tableHeader: { 
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa', 
            padding: '15px 10px', 
            borderBottom: `2px solid ${colors.border}` 
        },
        tableHeaderCell: { 
            padding: '12px 8px', 
            textAlign: 'left', 
            fontWeight: 'bold', 
            color: colors.text, 
            cursor: 'pointer', 
            userSelect: 'none', 
            position: 'relative',
            fontSize: '12px',
            whiteSpace: 'nowrap'
        },
        sortableHeader: { 
            display: 'flex', 
            alignItems: 'center', 
            gap: '5px' 
        },
        sortIcon: { 
            fontSize: '10px', 
            opacity: 0.7 
        },
        tableRow: { 
            borderBottom: `1px solid ${colors.border}`, 
            '&:hover': { backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa' } 
        },
        tableCell: { 
            padding: '8px', 
            color: colors.text, 
            verticalAlign: 'top',
            fontSize: '11px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '120px'
        },
        siteBadge: {
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 'bold',
            backgroundColor: colors.link,
            color: 'white'
        },
        noData: {
            textAlign: 'center',
            padding: '40px',
            color: colors.subtleText,
            fontStyle: 'italic'
        },
        uploadButton: {
            padding: '8px 16px',
            backgroundColor: '#20c997',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px'
        },
        paginationContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '15px 20px',
            borderTop: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa'
        },
        paginationInfo: {
            color: colors.subtleText,
            fontSize: '14px'
        },
        paginationControls: {
            display: 'flex',
            gap: '10px',
            alignItems: 'center'
        },
        paginationButton: {
            padding: '6px 12px',
            backgroundColor: colors.cardBg,
            color: colors.text,
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            minWidth: '40px'
        },
        paginationButtonDisabled: {
            padding: '6px 12px',
            backgroundColor: isDark ? '#333' : '#f5f5f5',
            color: colors.subtleText,
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            cursor: 'not-allowed',
            fontSize: '12px',
            minWidth: '40px'
        },
        pageNumbers: {
            display: 'flex',
            gap: '5px',
            alignItems: 'center'
        },
        pageNumber: {
            padding: '6px 10px',
            backgroundColor: colors.cardBg,
            color: colors.text,
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            minWidth: '30px',
            textAlign: 'center'
        },
        currentPage: {
            padding: '6px 10px',
            backgroundColor: colors.link,
            color: 'white',
            border: `1px solid ${colors.link}`,
            borderRadius: '4px',
            fontSize: '12px',
            minWidth: '30px',
            textAlign: 'center'
        }
    };
};

function FlightHourMonthlyTable({ theme, sites, onUpload }) {
    const styles = getStyles(theme);
    const [flightHourMonthlyData, setFlightHourMonthlyData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [siteFilter, setSiteFilter] = useState('');
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('asc');
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [pageSize] = useState(100);

    // Data is now filtered and sorted server-side
    const displayData = flightHourMonthlyData;

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const fetchFlightHourMonthlyData = useCallback((page = 1) => {
        setLoading(true);
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('page_size', pageSize);
        
        // Add search and filter parameters if they exist
        if (searchTerm) params.append('search', searchTerm);
        if (siteFilter) params.append('site_name', siteFilter);
        
        api.get(`/flight-hour-monthly/?${params.toString()}`)
            .then(res => {
                const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setFlightHourMonthlyData(data);
                
                // Handle pagination metadata
                if (res.data.count !== undefined) {
                    setTotalCount(res.data.count);
                    setTotalPages(Math.ceil(res.data.count / pageSize));
                } else {
                    setTotalCount(data.length);
                    setTotalPages(1);
                }
                setCurrentPage(page);
            })
            .catch(err => {
                console.error('Error fetching flight hour monthly data:', err);
                setAlert({ show: true, message: 'Failed to fetch Flight Hour Monthly data.', type: 'error' });
            })
            .finally(() => setLoading(false));
    }, [pageSize, searchTerm, siteFilter]);

    useEffect(() => {
        fetchFlightHourMonthlyData(1);
    }, [fetchFlightHourMonthlyData]);

    // Pagination functions
    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            fetchFlightHourMonthlyData(page);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            handlePageChange(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            handlePageChange(currentPage + 1);
        }
    };

    // Generate page numbers to display
    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
        
        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }
        
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    };

    const handleUploadClick = () => {
        onUpload('Flight Hour Monthly', 'Contains flight hour data with organization codes, document numbers, technician codes, aircraft serial numbers, timer codes, and flight hours');
    };

    return (
        <div style={styles.container}>
            <CustomAlert {...alert} onClose={() => setAlert({ show: false })} theme={theme} />
            
            <div style={styles.header}>
                <h2 style={styles.title}>Flight Hour Monthly Data</h2>
                <button onClick={handleUploadClick} style={styles.uploadButton}>
                    Upload New Data
                </button>
            </div>

            <div style={styles.controlsContainer}>
                <input
                    type="text"
                    placeholder="Search by Document No, Technician Code, or Aircraft Serial..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={styles.searchInput}
                />
                <select
                    value={siteFilter}
                    onChange={(e) => setSiteFilter(e.target.value)}
                    style={styles.filterSelect}
                >
                    <option value="">All Sites</option>
                    {Array.isArray(sites) && sites.map(site => (
                        <option key={site.id} value={site.name}>{site.name}</option>
                    ))}
                </select>
            </div>

            {loading ? (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                    <Spinner />
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.tableHeader}>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('org_cd')}>
                                        Org Code
                                        {sortField === 'org_cd' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('acft_serno')}>
                                        Aircraft Serial
                                        {sortField === 'acft_serno' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('create_dt')}>
                                        Create Date Time
                                        {sortField === 'create_dt' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('tmr_hrs')}>
                                        TMR Hrs
                                        {sortField === 'tmr_hrs' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('tmr_cd')}>
                                        TMR Code
                                        {sortField === 'tmr_cd' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('doc_no')}>
                                        Document No
                                        {sortField === 'doc_no' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('tec')}>
                                        TEC
                                        {sortField === 'tec' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('evnt_no')}>
                                        Event Number
                                        {sortField === 'evnt_no' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayData.length === 0 ? (
                                <tr>
                                    <td colSpan="8" style={styles.noData}>
                                        No Flight Hour Monthly data available. Upload data to get started.
                                    </td>
                                </tr>
                            ) : (
                                displayData.map((row, index) => (
                                    <tr key={`${row.site_name}-${row.doc_no}-${index}`} style={styles.tableRow}>
                                        <td style={styles.tableCell}>{row.org_cd}</td>
                                        <td style={styles.tableCell}>{row.acft_serno}</td>
                                        <td style={styles.tableCell}>{row.create_dt ? new Date(row.create_dt).toLocaleString() : ''}</td>
                                        <td style={styles.tableCell}>{row.tmr_hrs}</td>
                                        <td style={styles.tableCell}>{row.tmr_cd}</td>
                                        <td style={styles.tableCell}>{row.doc_no}</td>
                                        <td style={styles.tableCell}>{row.tec}</td>
                                        <td style={styles.tableCell}>{row.evnt_no}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
            
            {/* Pagination Controls */}
            {!loading && totalPages > 1 && (
                <div style={styles.paginationContainer}>
                    <div style={styles.paginationInfo}>
                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} records
                    </div>
                    <div style={styles.paginationControls}>
                        <button
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                            style={currentPage === 1 ? styles.paginationButtonDisabled : styles.paginationButton}
                        >
                            Previous
                        </button>
                        
                        <div style={styles.pageNumbers}>
                            {getPageNumbers().map(pageNum => (
                                <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    style={pageNum === currentPage ? styles.currentPage : styles.pageNumber}
                                >
                                    {pageNum}
                                </button>
                            ))}
                        </div>
                        
                        <button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            style={currentPage === totalPages ? styles.paginationButtonDisabled : styles.paginationButton}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FlightHourMonthlyTable;